// Originally generated by Lovable; hand-edited 2026-09-29 for the auth
// storage key below. Do not regenerate over it without re-applying that.
import { createClient, processLock } from '@supabase/supabase-js';
import type { Database } from './types';
import { isBarnApp } from '@/barnApps/registry';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * ⚠️ THE BARN APP KEEPS ITS OWN SESSION. TWO DOORS, TWO KEYS.
 *
 * The barn product and Market Intelligence have had separate front doors since
 * 2026-08-16 (`raisedbeef.com/my-barn/login` vs `/auth`), but they shared ONE
 * localStorage slot, so on raisedbeef.com — the same origin serves both — a
 * sign-in on either side evicted the other. That is not a testing nuisance, it
 * is the product breaking in both directions at once:
 *
 *   · Signing into a barn app while signed into the terminal REPLACED the
 *     terminal session. A barn account has no `profiles` row, so the platform
 *     resolved it to `free` and answered every route with /pricing — which
 *     reads exactly like "you were logged out and lost your subscription".
 *   · Opening a barn app while already signed into the terminal showed My Barn
 *     the PLATFORM's session. A genuine owner of one barn, signed in, looking
 *     at their own app, got the "not linked" card — because the login the app
 *     found belonged to a different barn. No sign-in form ever appeared, since
 *     from Supabase's point of view someone was already signed in.
 *
 * Both were reported on 2026-09-29 against Bluegrass and were the same bug.
 *
 * ⚠️ ONLY THE BARN SIDE IS OVERRIDDEN, and that asymmetry is deliberate.
 * Renaming the platform's key would sign out every existing terminal user for
 * nothing — the separation is achieved by moving ONE of the two.
 *
 * ⚠️ `isBarnApp()` IS THE SAME PREDICATE App.tsx BRANCHES ON. It must be, or
 * the key could disagree with which app actually rendered. It reads the build
 * flag, then `?barn=`, then the first path segment — all fixed for the life of
 * a document, and the barn branch bypasses the router entirely, so reaching a
 * barn app is always a full page load. Evaluating it once at module init is
 * therefore the same answer App.tsx gets at render.
 */
const AUTH_STORAGE_KEY = isBarnApp() ? 'rb-barn-auth' : undefined;

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    // undefined = Supabase's default (`sb-<ref>-auth-token`), which is what the
    // platform has always used and what its live sessions are stored under.
    ...(AUTH_STORAGE_KEY ? { storageKey: AUTH_STORAGE_KEY } : {}),
    persistSession: true,
    autoRefreshToken: true,
    // ⚠️ ONE LOCK PER TAB ON THE WEBSITE, NOT ONE SHARED BY EVERY TAB (owner,
    // 2026-10-14: "when I click on a page it goes to a signed out status then
    // it loads for a couple seconds then goes to a signed in status" — and
    // with every other raisedbeef.com tab closed, "it's fast").
    //
    // Supabase's default lock is the browser's cross-tab lock. Every page load
    // reads the session under it, so a tab the browser has paused in the
    // background while holding it makes EVERY new page wait — up to the
    // library's 5-second timeout — before it knows who you are. The header
    // reads "Sign in" and gated pages sit blank for that whole wait.
    //
    // A per-tab lock still orders the reads and refreshes inside one tab. Two
    // tabs renewing at the same moment both send the same refresh token, which
    // Supabase accepts inside its reuse window, and each tab picks up the
    // other's new session from storage.
    //
    // The barn apps keep the default: a native app is one webview, so it has
    // no other tabs to wait on, and Beaver County's setup stays untouched.
    ...(isBarnApp() ? {} : { lock: processLock }),
  }
});
